import get from 'lodash.get';
import { format, isValid, parse, parseISO, startOfDay } from 'date-fns';

export const parseAsrDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return isValid(value) ? value : null;

  const numericValue = Number(value);
  if (Number.isFinite(numericValue) && String(value).trim() !== '') {
    const timestamp = numericValue < 100000000000 ? numericValue * 1000 : numericValue;
    const parsedNumeric = new Date(timestamp);
    return isValid(parsedNumeric) ? parsedNumeric : null;
  }

  const text = String(value).trim();
  const isoDate = parseISO(text);
  if (isValid(isoDate)) return isoDate;

  const dayFirstDate = parse(text, 'dd/MM/yyyy', new Date());
  if (isValid(dayFirstDate)) return dayFirstDate;

  const parsedDate = new Date(text);
  return isValid(parsedDate) ? parsedDate : null;
};

export const isAsrDatePast = (value, today = new Date()) => {
  const parsedDate = parseAsrDate(value);
  return Boolean(parsedDate && startOfDay(parsedDate) < startOfDay(today));
};

export const formatAsrDate = (value, fallback = 'Not recorded') => {
  const parsed = parseAsrDate(value);
  return parsed ? format(parsed, 'dd/MM/yyyy') : fallback;
};

const hasDate = (value) => value !== null && value !== undefined && value !== '';

const mapSensorReadings = (system, readings) =>
  (Array.isArray(readings) ? readings : [])
    .filter((reading) => hasDate(reading?.calibrationDueDate) || hasDate(reading?.expiryDate))
    .map((reading) => ({
      system,
      sensor: reading?.gasType || 'Sensor',
      calibrationDate: formatAsrDate(reading?.calibrationDueDate),
      calibrationDateValue: reading?.calibrationDueDate,
      expiryDate: formatAsrDate(reading?.expiryDate),
      expiryDateValue: reading?.expiryDate,
    }));

export const getGasSensorExpiryRows = (serviceForm) => {
  const chamberOptionNodeReadings = get(serviceForm, 'chamberOptions.extGasNode.nodeSensorReadings');
  const legacyNodeReadings = get(serviceForm, 'consumables.extGasNode.nodeSensorReadings');
  const auraFxRows = mapSensorReadings(
    'Aura-FX',
    get(serviceForm, 'consumables.auraFXGasMonitoring.sensorReadings', []),
  );
  const externalNodeRows = mapSensorReadings(
    'External Gas Node',
    Array.isArray(chamberOptionNodeReadings) ? chamberOptionNodeReadings : legacyNodeReadings,
  );

  return [...auraFxRows, ...externalNodeRows];
};

export const getConsumablesExpiryRows = (serviceForm) => {
  const cartridgeChemicals = get(serviceForm, 'consumables.cartridgeScrubberChemicals', {});
  const freePourChemicals = get(serviceForm, 'consumables.freePourScrubberChemicals', {});
  const gasTubes = get(serviceForm, 'consumables.manualGasSamplingPump', {});
  const values = [
    ['Oxygen Candle', get(serviceForm, 'consumables.oxygenCandle.oxygenCandleExpiry')],
    ['CO2 Cartridge', cartridgeChemicals?.co2Expiry || freePourChemicals?.co2Expiry],
    ['CO Cartridge', cartridgeChemicals?.coExpiry || freePourChemicals?.coExpiry],
    ['O2 Gas Tubes', gasTubes?.o2GasTubesExpiry],
    ['CO2 Gas Tubes', gasTubes?.co2GasTubesExpiry],
    ['CO Gas Tubes', gasTubes?.coGasTubesExpiry],
  ];

  return values
    .filter(([, expiryDate]) => hasDate(expiryDate))
    .map(([consumable, expiryDate]) => ({
      consumable,
      expiryDate: formatAsrDate(expiryDate),
      expiryDateValue: expiryDate,
    }));
};
